/**
 * Kilitli Hesaplar kartı (admin).
 *
 * `auth_credentials.login_blocked = true` olan tüm personeli tek bakışta gösterir.
 * Her satır için tek tıkla "Kilidi Aç" ve "Geçici Kod Üret" işlemleri.
 *
 * Eylemler mevcut endpoint'leri kullanır:
 *  - PUT /api/personnel/{id}/login-blocked  (blocked:false)
 *  - POST /api/personnel/{id}/issue-temporary-login
 *
 * Geçici kodla giriş yapan kullanıcı, backend tarafında
 * `credential_setup_completed=false` olarak işaretlenir → ilk işlem olarak
 * yeni şifre ve kurtarma parolası belirleme ekranı açılır.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  KeyRound,
  Loader2,
  Lock,
  RefreshCw,
  ShieldAlert,
  Unlock,
  Copy,
} from "lucide-react";
import { BACKEND_URL } from "@/config";
import { normalizeSearchText } from "@/utils/stringUtils";
import { buildApiCandidates, shouldFailoverBackendRequest } from "@/backendBases";

const API = BACKEND_URL;

const ROLE_LABEL = {
  admin: "Yönetici",
  mudur: "Müdür",
  mudur_yardimcisi: "Müdür Yrd.",
  bolge_muhendisi: "Bölge Mühendisi",
  formen: "Formen",
  park_gorevlisi: "Park Görevlisi",
};

function formatRole(role) {
  if (!role) return "—";
  return ROLE_LABEL[role] || role;
}

function formatDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("tr-TR");
  } catch (_) {
    return iso;
  }
}

async function _getWithFailover(path) {
  const candidates = buildApiCandidates(API);
  let lastErr = null;
  for (const base of candidates) {
    const url = base ? `${base}${path}` : path;
    try {
      return await axios.get(url, { withCredentials: true, timeout: 15000 });
    } catch (err) {
      lastErr = err;
      if (shouldFailoverBackendRequest(err)) continue;
      throw err;
    }
  }
  throw lastErr || new Error("Sunucuya ulaşılamadı.");
}

async function _putWithFailover(path, body) {
  const candidates = buildApiCandidates(API);
  let lastErr = null;
  for (const base of candidates) {
    const url = base ? `${base}${path}` : path;
    try {
      return await axios.put(url, body, {
        withCredentials: true,
        timeout: 15000,
      });
    } catch (err) {
      lastErr = err;
      if (shouldFailoverBackendRequest(err)) continue;
      throw err;
    }
  }
  throw lastErr || new Error("Sunucuya ulaşılamadı.");
}

async function _postWithFailover(path, body) {
  const candidates = buildApiCandidates(API);
  let lastErr = null;
  for (const base of candidates) {
    const url = base ? `${base}${path}` : path;
    try {
      return await axios.post(url, body || {}, {
        withCredentials: true,
        timeout: 20000,
      });
    } catch (err) {
      lastErr = err;
      if (shouldFailoverBackendRequest(err)) continue;
      throw err;
    }
  }
  throw lastErr || new Error("Sunucuya ulaşılamadı.");
}

export default function LockedAccountsCard() {
  const [items, setItems] = useState([]);
  const [threshold, setThreshold] = useState(3);
  const [loading, setLoading] = useState(false);
  const [errorText, setErrorText] = useState("");
  const [search, setSearch] = useState("");
  const [reasonFilter, setReasonFilter] = useState("all"); // all | auto | manual
  // Kilit açma / geçici kod yetkisi yalnızca admin'de (backend can_manage döner).
  // Müdür yalnızca görüntüler.
  const [canManage, setCanManage] = useState(false);

  const [unlockTarget, setUnlockTarget] = useState(null); // {personnel_id, name}
  const [unlocking, setUnlocking] = useState(false);

  const [tempCodeTarget, setTempCodeTarget] = useState(null); // {personnel_id, name}
  const [tempCodeLoading, setTempCodeLoading] = useState(false);
  const [issuedCode, setIssuedCode] = useState(null); // {code, expires_at, name}

  const fetchList = useCallback(async () => {
    setLoading(true);
    setErrorText("");
    try {
      const res = await _getWithFailover("/api/login-management/locked-accounts");
      const data = res?.data || {};
      setItems(Array.isArray(data.items) ? data.items : []);
      if (typeof data.threshold === "number") setThreshold(data.threshold);
      setCanManage(Boolean(data.can_manage));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : err?.message || "Kilitli hesaplar alınamadı.";
      setErrorText(msg);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const filteredItems = useMemo(() => {
    const q = normalizeSearchText(search);
    return items.filter((it) => {
      if (reasonFilter !== "all" && it.block_reason !== reasonFilter) return false;
      if (!q) return true;
      const name = normalizeSearchText(it.name);
      const role = normalizeSearchText(it.role);
      const pid = normalizeSearchText(it.personnel_id);
      return name.includes(q) || role.includes(q) || pid.includes(q);
    });
  }, [items, search, reasonFilter]);

  const executeUnlock = async () => {
    if (!unlockTarget?.personnel_id) return;
    setUnlocking(true);
    try {
      await _putWithFailover(
        `/api/personnel/${unlockTarget.personnel_id}/login-blocked`,
        { blocked: false }
      );
      toast.success(`${unlockTarget.name || "Kullanıcı"} — kilit açıldı.`);
      setUnlockTarget(null);
      await fetchList();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg =
        typeof detail === "string" ? detail : err?.message || "Kilit açılamadı.";
      toast.error(msg);
    } finally {
      setUnlocking(false);
    }
  };

  const issueTempCode = async (row) => {
    if (!row?.personnel_id) return;
    setTempCodeLoading(true);
    setTempCodeTarget(row);
    setIssuedCode(null);
    try {
      const res = await _postWithFailover(
        `/api/personnel/${row.personnel_id}/issue-temporary-login`,
        {}
      );
      const data = res?.data || {};
      setIssuedCode({
        code: data.code || data.token || "",
        expires_at: data.expires_at || null,
        name: row.name || "",
        message: data.message || "",
      });
      toast.success("Geçici giriş kodu oluşturuldu.");
      await fetchList();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg =
        typeof detail === "string" ? detail : err?.message || "Kod üretilemedi.";
      toast.error(msg);
      setTempCodeTarget(null);
    } finally {
      setTempCodeLoading(false);
    }
  };

  const copyCode = async () => {
    if (!issuedCode?.code) return;
    try {
      await navigator.clipboard.writeText(issuedCode.code);
      toast.success("Kod panoya kopyalandı.");
    } catch (_) {
      toast.error("Panoya kopyalanamadı.");
    }
  };

  return (
    <Card data-testid="locked-accounts-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldAlert className="h-5 w-5 text-red-600" />
          Kilitli Hesaplar
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          {threshold} ardışık hatalı girişten sonra otomatik kilitlenen veya admin
          tarafından engellenen hesaplar. Tek tıkla kilidi açabilir veya tek
          kullanımlık geçici giriş kodu üretebilirsiniz. Geçici kodla giriş
          yapan kullanıcı, ilk işlem olarak şifresini ve kurtarma parolasını
          yenilemek zorunda kalır.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[220px]">
            <Input
              placeholder="Ada, role veya ID'ye göre ara..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="locked-search"
            />
          </div>
          <div>
            <SearchableSelect
              className="text-sm"
              value={reasonFilter}
              onChange={(v) => setReasonFilter(v)}
              allLabel="Tüm kilitler"
              allValue="all"
              searchPlaceholder="Ara..."
              options={[
                { id: "auto", name: "Sadece otomatik" },
                { id: "manual", name: "Sadece manuel" },
              ]}
            />
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={fetchList}
            disabled={loading}
            data-testid="locked-refresh"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            <span className="ml-2">Yenile</span>
          </Button>
          <div className="text-xs text-muted-foreground ml-auto">
            Toplam: <strong>{filteredItems.length}</strong>
            {filteredItems.length !== items.length && (
              <span className="ml-1">/ {items.length}</span>
            )}
          </div>
        </div>

        {errorText && (
          <div className="text-sm text-red-600 border border-red-200 rounded p-2 bg-red-50">
            {errorText}
          </div>
        )}

        {!canManage && !errorText && (
          <div className="text-xs text-amber-800 border border-amber-200 rounded p-2 bg-amber-50">
            Yalnızca görüntüleme yetkiniz var. Kilidi açma ve geçici kod üretme işlemleri yalnızca admin tarafından yapılabilir.
          </div>
        )}

        <div className="rounded-md border max-h-[min(70vh,560px)] overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Ad Soyad</TableHead>
                <TableHead>Rol</TableHead>
                <TableHead className="text-center">Hatalı Deneme</TableHead>
                <TableHead>Son Hatalı Giriş</TableHead>
                <TableHead>Kilit Nedeni</TableHead>
                <TableHead>Kilitleyen</TableHead>
                {canManage && <TableHead className="text-right">İşlemler</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredItems.length === 0 && !loading ? (
                <TableRow>
                  <TableCell
                    colSpan={canManage ? 7 : 6}
                    className="text-center text-muted-foreground py-8"
                  >
                    {items.length === 0
                      ? "Şu anda kilitli hesap yok."
                      : "Filtreye uyan kayıt yok."}
                  </TableCell>
                </TableRow>
              ) : (
                filteredItems.map((row) => (
                  <TableRow
                    key={row.personnel_id}
                    data-testid={`locked-row-${row.personnel_id}`}
                  >
                    <TableCell className="font-medium">
                      {row.name || "—"}
                      {row.has_active_temp_token && (
                        <Badge
                          variant="outline"
                          className="ml-2 text-xs text-amber-700 border-amber-300 bg-amber-50"
                        >
                          Aktif kod var
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {formatRole(row.role)}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge
                        variant={row.login_failed_attempts >= threshold ? "destructive" : "secondary"}
                      >
                        {row.login_failed_attempts ?? 0}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs whitespace-nowrap">
                      {formatDateTime(row.last_failed_login_at)}
                    </TableCell>
                    <TableCell>
                      {row.block_reason === "auto" ? (
                        <Badge className="bg-red-100 text-red-800 border-red-200">
                          <Lock className="h-3 w-3 mr-1" />
                          Otomatik
                        </Badge>
                      ) : (
                        <Badge variant="outline">Manuel</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      <div className="flex flex-col">
                        <span>{row.blocked_by_name || "—"}</span>
                        {row.blocked_at && (
                          <span className="text-[11px] text-muted-foreground whitespace-nowrap">
                            {formatDateTime(row.blocked_at)}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    {canManage && (
                      <TableCell className="text-right space-x-2 whitespace-nowrap">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => setUnlockTarget(row)}
                          data-testid={`unlock-btn-${row.personnel_id}`}
                        >
                          <Unlock className="h-4 w-4 mr-1" />
                          Kilidi Aç
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => issueTempCode(row)}
                          disabled={
                            tempCodeLoading &&
                            tempCodeTarget?.personnel_id === row.personnel_id
                          }
                          data-testid={`temp-code-btn-${row.personnel_id}`}
                        >
                          {tempCodeLoading &&
                          tempCodeTarget?.personnel_id === row.personnel_id ? (
                            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                          ) : (
                            <KeyRound className="h-4 w-4 mr-1" />
                          )}
                          Geçici Kod
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>

      <AlertDialog
        open={!!unlockTarget}
        onOpenChange={(open) => !open && setUnlockTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Kilidi Aç</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{unlockTarget?.name || "Bu kullanıcı"}</strong> için giriş
              engeli kaldırılacak ve hatalı deneme sayacı sıfırlanacak. Devam
              edilsin mi?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={unlocking}>İptal</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                executeUnlock();
              }}
              disabled={unlocking}
            >
              {unlocking ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  Açılıyor...
                </>
              ) : (
                "Kilidi Aç"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={!!issuedCode}
        onOpenChange={(open) => {
          if (!open) {
            setIssuedCode(null);
            setTempCodeTarget(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Geçici Giriş Kodu</DialogTitle>
            <DialogDescription>
              {issuedCode?.name && (
                <>
                  <strong>{issuedCode.name}</strong> için tek kullanımlık kod
                  üretildi.{" "}
                </>
              )}
              Kullanıcı giriş yaptıktan sonra ilk işlem olarak şifresini ve
              kurtarma parolasını belirlemek zorundadır. Bu kod yalnızca bir
              kez ve aşağıdaki süre içinde kullanılabilir.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <div className="flex-1 text-3xl font-mono font-bold tracking-widest text-center py-4 px-3 bg-slate-100 rounded-md border">
                {issuedCode?.code || "—"}
              </div>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={copyCode}
                title="Kopyala"
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
            {issuedCode?.expires_at && (
              <p className="text-xs text-muted-foreground text-center">
                Son kullanma: {formatDateTime(issuedCode.expires_at)}
              </p>
            )}
            {issuedCode?.message && (
              <p className="text-sm text-muted-foreground">{issuedCode.message}</p>
            )}
          </div>
          <DialogFooter>
            <Button
              type="button"
              onClick={() => {
                setIssuedCode(null);
                setTempCodeTarget(null);
              }}
            >
              Kapat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
